import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Label, Input, Badge, Alert, Card, CardBody, FormGroup } from "reactstrap";
import useProjects from "../../Hooks/useProjects";
import { checkUserConflictsApi } from "../../apiServices/leadAssignment.services";
import { handleUserChangesApi } from "../../apiServices/automationRule.services";
import { toast } from "react-toastify";

const CreateRuleModal = ({ isOpen, toggle, onSubmit, editData }) => {
  const { projects } = useProjects({ onlyHavingForms: true });
  const [allowedUsers, setAllowedUsers] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [removedUsers, setRemovedUsers] = useState([]);
  const [removedUserConfigs, setRemovedUserConfigs] = useState({});
  const [formData, setFormData] = useState({
    ruleName: "", projectIds: [], includeUsers: [], autoAssignUpcomingLeads: false,
    lockPreviousOwners: false, autoCreateTask: false, taskTitle: "Follow Up Lead",
    taskDescription: "", taskPriority: "medium", dueAfterMinutes: 60,
  });

  useEffect(() => {
    if (editData) {
      setFormData({
        ruleName: editData?.ruleName || "",
        projectIds: editData?.projectIds || [],
        includeUsers: editData?.includeUsers || [],
        autoAssignUpcomingLeads: editData?.autoAssignUpcomingLeads || false,
        lockPreviousOwners: editData?.lockPreviousOwners || false,
        autoCreateTask: editData?.autoCreateTask || false,
        taskTitle: editData?.taskTitle || "Follow Up Lead",
        taskDescription: editData?.taskDescription || "",
        taskPriority: editData?.taskPriority || "medium",
        dueAfterMinutes: editData?.dueAfterMinutes || 60,
      });
      setRemovedUsers([]);
      setRemovedUserConfigs({});
    }
  }, [editData]);

  useEffect(() => {
    if (editData?.projectIds?.length) {
      handleProjectsChange(editData.projectIds.map((p) => ({ label: p.projectName, value: p._id })));
    }
  }, [editData]);

  const handleProjectsChange = async (selected) => {
    const projectIds = selected?.map((x) => x.value) || [];
    setFormData((p) => ({ ...p, projectIds }));
    try {
      const res = await checkUserConflictsApi({ projectIds });
      const selectedUsers = editData?.includeUsers || [];
      setAllowedUsers([
        ...(res?.data?.allowedUsers || []),
        ...selectedUsers.filter((su) => !(res?.data?.allowedUsers || []).some((au) => au._id === su._id)),
      ]);
      setBlockedUsers(res?.data?.blockedUsers || []);
    } catch (err) {
      console.log(err);
    }
  };

  const getActiveUserOptions = () => {
    const list = [];
    const seen = new Set();

    (formData.includeUsers || []).forEach((u) => {
      if (u.role === "super_admin") return;
      const id = String(u._id || u.value || u);
      if (!seen.has(id)) {
        seen.add(id);
        const name = u.firstName ? `${u.firstName} ${u.lastName}`.trim() : (u.label || u.name || id);
        list.push({ _id: id, name, email: u.email || "" });
      }
    });

    (allowedUsers || []).forEach((u) => {
      if (u.role === "super_admin") return;
      const id = String(u._id || u.value || u);
      if (!seen.has(id)) {
        seen.add(id);
        const name = u.firstName ? `${u.firstName} ${u.lastName}`.trim() : (u.label || u.name || id);
        list.push({ _id: id, name, email: u.email || "" });
      }
    });

    return list;
  };

  const handleIncludeUsersChange = (selected) => {
    const newIncludeUsers = selected?.map((x) => ({
      _id: x.value, firstName: x.label.split(" ")[0], lastName: x.label.split(" ")[1] || "", email: x.email || "",
    })) || [];

    if (editData && editData.includeUsers) {
      const newSelectedIds = newIncludeUsers.map((u) => String(u._id));
      const newlyRemoved = (editData.includeUsers || []).filter((ou) => !newSelectedIds.includes(String(ou._id || ou)));
      setRemovedUsers(newlyRemoved);

      const activeOpts = newIncludeUsers.length ? newIncludeUsers : getActiveUserOptions();
      const defaultTargetId = activeOpts.length ? activeOpts[0]._id : "";

      const newConfigs = {};
      newlyRemoved.forEach((u) => {
        const uId = u._id || u;
        const uName = u.firstName ? `${u.firstName} ${u.lastName}` : (u.label || u.email || uId);
        newConfigs[uId] = removedUserConfigs[uId] || {
          userId: uId, userName: uName, actionType: "pause",
          pauseType: "infinite", durationMinutes: 60, customDate: "", transferType: "all_active_users",
          transferToUser: defaultTargetId, reason: "",
        };
      });
      setRemovedUserConfigs(newConfigs);
    }
    setFormData((p) => ({ ...p, includeUsers: newIncludeUsers }));
  };

  const handleConfigChange = (userId, field, value) => {
    setRemovedUserConfigs((prev) => ({
      ...prev, [userId]: { ...prev[userId], [field]: value },
    }));
  };

  const handleSubmit = async () => {
    try {
      if (editData && removedUsers.length > 0) {
        const userChangesPayload = Object.values(removedUserConfigs).map((cfg) => ({
          userId: cfg.userId, actionType: cfg.actionType, pauseType: cfg.pauseType,
          pausedUntil: cfg.actionType === "pause" && cfg.pauseType === "scheduled" && cfg.customDate ? new Date(cfg.customDate).toISOString() : undefined,
          durationMinutes: cfg.actionType === "pause" && cfg.pauseType === "scheduled" && !cfg.customDate ? cfg.durationMinutes : undefined,
          transferType: cfg.transferType, transferToUser: cfg.transferToUser, reason: cfg.reason,
        }));
        await handleUserChangesApi(editData._id, { userChanges: userChangesPayload });
      }

      await onSubmit({
        ...formData,
        projectIds: formData.projectIds?.map((p) => p._id || p),
        includeUsers: formData.includeUsers?.map((u) => u._id || u),
      });

      toggle();
    } catch (error) {
      toast.error(error?.message || error?.response?.data?.message || "Something went wrong");
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>{editData ? "Edit Automation Rule" : "Create Automation Rule"}</ModalHeader>
      <ModalBody>
        <Row className="g-3">
          <Col md={6}>
            <Label> Rule Name </Label>
            <Input placeholder="E.g. Auto Assign Rule 1" value={formData.ruleName} onChange={(e) => setFormData((p) => ({ ...p, ruleName: e.target.value }))} />
          </Col>

          <Col md={6}>
            <Label> Project </Label>
            <Select
              isMulti closeMenuOnSelect={false}
              value={(formData.projectIds || []).map((p) => ({
                label: typeof p === "object" ? p?.projectName : projects.find((x) => x._id === p)?.projectName || "",
                value: typeof p === "object" ? p?._id : p,
              }))}
              options={projects.map((p) => ({ label: p.projectName, value: p._id }))}
              onChange={(selected) => {
                setFormData((prev) => ({ ...prev, projectIds: selected?.map((x) => ({ _id: x.value, projectName: x.label })) || [] }));
                handleProjectsChange(selected);
              }}
            />
          </Col>

          <Col md={12}>
            <Label> Include Users </Label>
            <Select
              isMulti
              value={formData.includeUsers?.map((u) => ({ label: u.firstName ? `${u.firstName} ${u.lastName}` : (u.label || u._id), value: u._id || u, email: u.email }))}
              options={[
                ...allowedUsers.map((u) => ({ label: `${u.firstName} ${u.lastName}`, value: u._id, email: u.email, isDisabled: false })),
                ...blockedUsers.map((u) => ({ label: `${u.firstName} ${u.lastName} (Already Assigned)`, value: u._id, email: u.email, isDisabled: true })),
              ]}
              onChange={handleIncludeUsersChange}
            />
          </Col>

          {/* Embedded Permanent vs Temporary Action Panel inside Edit Modal */}
          {editData && removedUsers.length > 0 && (
            <Col md={12}>
              <Card className="border border-warning bg-warning-subtle">
                <CardBody className="p-3">
                  <h6 className="fw-bold text-dark mb-2"><i className="mdi mdi-alert-circle text-warning me-1" /> Config Action for Removed User(s) ({removedUsers.length})</h6>
                  <p className="small text-muted mb-3">Choose whether removed users are taking a Temporary Pause (purani leads safe rahengi) or being Permanently Removed (leads transfer hongi).</p>

                  {removedUsers.map((u) => {
                    const uId = u._id || u;
                    const uName = u.firstName ? `${u.firstName} ${u.lastName}` : (u.label || u.email || uId);
                    const cfg = removedUserConfigs[uId] || {};
                    const activeOpts = getActiveUserOptions();

                    return (
                      <div key={uId} className="bg-white p-3 rounded border mb-2">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <strong>👤 {uName} {u.email ? `(${u.email})` : ""}</strong>
                        </div>
                        <Row className="g-2">
                          <Col md={6}>
                            <Label className="small fw-semibold mb-1">Action Mode:</Label>
                            <div>
                              <FormGroup check inline className="me-3">
                                <Label check className="small">
                                  <Input type="radio" name={`action_${uId}`} checked={cfg.actionType === "pause"} onChange={() => handleConfigChange(uId, "actionType", "pause")} /> ⏸️ Temporary Pause
                                </Label>
                              </FormGroup>
                              <FormGroup check inline>
                                <Label check className="small text-danger">
                                  <Input type="radio" name={`action_${uId}`} checked={cfg.actionType === "permanent_remove"} onChange={() => handleConfigChange(uId, "actionType", "permanent_remove")} /> 🔴 Permanent Removal
                                </Label>
                              </FormGroup>
                            </div>
                          </Col>

                          <Col md={6}>
                            {cfg.actionType === "pause" ? (
                              <div>
                                <Label className="small fw-semibold mb-1">Pause Duration:</Label>
                                <Row className="g-1">
                                  <Col md={6}>
                                    <Input type="select" bsSize="sm" value={cfg.pauseType} onChange={(e) => handleConfigChange(uId, "pauseType", e.target.value)}>
                                      <option value="infinite">♾️ Until I turn ON</option>
                                      <option value="scheduled">⏱️ Scheduled Time</option>
                                    </Input>
                                  </Col>
                                  {cfg.pauseType === "scheduled" && (
                                    <Col md={6}>
                                      <Input type="select" bsSize="sm" value={cfg.durationMinutes} onChange={(e) => handleConfigChange(uId, "durationMinutes", Number(e.target.value))}>
                                        <option value={60}>1 Hour</option>
                                        <option value={240}>4 Hours</option>
                                        <option value={1440}>1 Day (24h)</option>
                                      </Input>
                                    </Col>
                                  )}
                                </Row>
                              </div>
                            ) : (
                              <div>
                                <Label className="small fw-semibold mb-1 text-danger">Lead Transfer Target:</Label>
                                <Input type="select" bsSize="sm" value={cfg.transferType} onChange={(e) => handleConfigChange(uId, "transferType", e.target.value)}>
                                  <option value="all_active_users">Distribute Equally to All Active Members</option>
                                  <option value="individual_user">Transfer to Specific Member</option>
                                  <option value="unassigned">Make Leads Unassigned</option>
                                </Input>
                                {cfg.transferType === "individual_user" && (
                                  <Input
                                    type="select"
                                    bsSize="sm"
                                    className="mt-1"
                                    value={cfg.transferToUser || (activeOpts[0]?._id || "")}
                                    onChange={(e) => handleConfigChange(uId, "transferToUser", e.target.value)}
                                  >
                                    {activeOpts.map((au) => (
                                      <option key={au._id} value={au._id}>{au.name} {au.email ? `(${au.email})` : ""}</option>
                                    ))}
                                  </Input>
                                )}
                              </div>
                            )}
                          </Col>
                        </Row>
                      </div>
                    );
                  })}
                </CardBody>
              </Card>
            </Col>
          )}

          <Col xs={12}>
            <div className="d-flex flex-wrap align-items-center gap-4">
              <div className="d-flex align-items-center">
                <input id="upcomingLeads" type="checkbox" switch="primary" checked={formData.autoAssignUpcomingLeads} onChange={(e) => setFormData((p) => ({ ...p, autoAssignUpcomingLeads: e.target.checked }))} />
                <label htmlFor="upcomingLeads" data-on-label="" data-off-label="" className="mb-0" />
                <span className="ms-2 fw-semibold mb-1">Upcoming Leads</span>
              </div>
              <div className="d-flex align-items-center">
                <input id="lockOwners" type="checkbox" switch="primary" checked={formData.lockPreviousOwners} onChange={(e) => setFormData((p) => ({ ...p, lockPreviousOwners: e.target.checked }))} />
                <label htmlFor="lockOwners" data-on-label="" data-off-label="" className="mb-0" />
                <span className="ms-2 fw-semibold">Lock Owners</span>
              </div>
              <div className="d-flex align-items-center">
                <input id="autoTasks" type="checkbox" switch="primary" checked={formData.autoCreateTask} onChange={(e) => setFormData((p) => ({ ...p, autoCreateTask: e.target.checked }))} />
                <label htmlFor="autoTasks" data-on-label="" data-off-label="" className="mb-0" />
                <span className="ms-2 fw-semibold">Auto Tasks</span>
              </div>
            </div>
          </Col>

          <Col md={6}>
            <Label> Task Title </Label>
            <Input value={formData.taskTitle} onChange={(e) => setFormData((p) => ({ ...p, taskTitle: e.target.value }))} />
          </Col>
          <Col md={6}>
            <Label> Task Priority </Label>
            <Input type="select" value={formData.taskPriority} onChange={(e) => setFormData((p) => ({ ...p, taskPriority: e.target.value }))}>
              <option value="low"> Low </option>
              <option value="medium"> Medium </option>
              <option value="high"> High </option>
            </Input>
          </Col>
          <Col md={12}>
            <Label> Task Description </Label>
            <Input type="textarea" rows={3} value={formData.taskDescription} onChange={(e) => setFormData((p) => ({ ...p, taskDescription: e.target.value }))} />
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle}>Close</Button>
        <Button color="primary" onClick={handleSubmit}>{editData ? "Update Rule" : "Save Rule"}</Button>
      </ModalFooter>
    </Modal>
  );
};

export default CreateRuleModal;
